/*
 * ARL Automatic Free Gift With Purchase — tiered
 * Invariant, per tier: cart holds that tier's gift IFF real (non-gift) item
 * quantity >= 1 AND real-items subtotal (cents) >= tier.minSubtotal.
 * Enforced after every cart mutation, self-healing.
 *
 * Config (set in theme.liquid before this script loads):
 *   window.FREE_GIFTS = [
 *     { id: <variant_id>, handle: '<product-handle>', toast: '<msg>', minSubtotal: 0 },
 *     { id: <variant_id>, handle: '<product-handle>', toast: '<msg>', minSubtotal: 25000 } // cents = $250
 *   ];
 * Back-compat: a single window.FREE_GIFT = { id, handle, toast } object is
 * still accepted and treated as one tier with minSubtotal: 0.
 *
 * Also hides each configured gift product from predictive search (exact handle match).
 */
(function () {
  // Never run inside the theme editor — patching fetch + mutating the cart breaks it.
  if (window.Shopify && window.Shopify.designMode) return;

  /* ---------- config ---------- */
  var GIFTS = [];
  if (Array.isArray(window.FREE_GIFTS)) {
    GIFTS = window.FREE_GIFTS.filter(function (g) { return g && g.id; }).map(function (g) {
      return {
        id: g.id,
        handle: g.handle || '',
        name: g.name || g.handle || 'gift',
        toast: g.toast || 'A free gift was added to your cart.',
        title: g.title || 'Free gift added',
        minSubtotal: typeof g.minSubtotal === 'number' ? g.minSubtotal : 0
      };
    });
  } else if (window.FREE_GIFT && window.FREE_GIFT.id) {
    var legacy = window.FREE_GIFT;
    GIFTS = [{
      id: legacy.id,
      handle: legacy.handle || '',
      name: legacy.name || legacy.handle || 'gift',
      toast: legacy.toast || 'A free gift was added to your cart.',
      title: 'Free gift added',
      minSubtotal: 0
    }];
  }
  if (!GIFTS.length) return;

  var GIFT_IDS = GIFTS.map(function (g) { return g.id; });
  var GIFT_HANDLES = GIFTS.map(function (g) { return g.handle; }).filter(Boolean);

  // Pristine fetch captured BEFORE we patch window.fetch — used for all our own calls (no loops).
  var realFetch = window.fetch.bind(window);

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function u(path) { return (ROOT + path).replace(/\/{2,}/g, '/'); }

  var busy = false;
  var queued = false;

  /* ---------- helpers ---------- */
  function jget(url) {
    return realFetch(url, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function jpost(url, body) {
    return realFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function isGiftLine(it) {
    return GIFT_IDS.indexOf(it.id) !== -1 || GIFT_IDS.indexOf(it.variant_id) !== -1;
  }

  /* ---------- Toast ---------- */
  var toastWrap;
  function toast(opts) {
    opts = opts || {};
    if (!toastWrap) {
      toastWrap = document.createElement('div');
      toastWrap.className = 'arl-toast-wrap';
      toastWrap.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastWrap);
    }
    var icon = opts.gift
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9M12 8v13M12 8S9.5 3 7 5s2 3 5 3zM12 8s2.5-5 5-3-2 3-5 3z"/></svg>'
      : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
    var t = document.createElement('div');
    t.className = 'arl-toast' + (opts.gift ? ' arl-toast--gift' : '');
    t.innerHTML =
      '<span class="arl-toast__icon">' + icon + '</span>' +
      '<span class="arl-toast__body">' +
      '<span class="arl-toast__title">' + esc(opts.title || 'Added to cart') + '</span>' +
      (opts.message ? '<span class="arl-toast__msg">' + esc(opts.message) + '</span>' : '') +
      '</span>';
    toastWrap.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-in'); });
    var kill = function () { t.classList.remove('is-in'); setTimeout(function () { if (t.parentNode) t.remove(); }, 320); };
    var timer = setTimeout(kill, opts.duration || 3800);
    t.addEventListener('click', function () { clearTimeout(timer); kill(); });
  }
  window.arlToast = toast;

  /* ---------- cart count ---------- */
  function updateCounts(count) {
    document.querySelectorAll('.cart-bubble__text-count, [ref="cartBubbleCount"]').forEach(function (el) {
      el.textContent = count < 100 ? String(count) : '';
      el.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('.cart-bubble').forEach(function (el) {
      el.classList.toggle('visually-hidden', count === 0);
    });
    document.querySelectorAll('.cart-count, [data-cart-count]').forEach(function (el) {
      el.textContent = String(count);
    });
  }

  /* ---------- patch cart-items-component DOM from a sections{} object ---------- */
  // Shopify's cart/add.js and cart/update.js both accept an optional
  // "sections" field in the POST body and, when present, return rendered
  // HTML for each requested section id under response.sections[id] — this
  // is the same mechanism Fabric's own cart-items-component uses. A plain
  // GET to /cart?sections=... was tried first but this store's /cart route
  // ignores the sections param when Accept: application/json is set and
  // just returns the cart.js object instead — so patching now happens
  // directly from the mutation response itself, no separate GET needed.
  function patchSections(sectionsObj) {
    if (!sectionsObj || typeof sectionsObj !== 'object') {
      console.warn('[ARL-GIFT-DEBUG] patchSections() called with no sections object');
      return;
    }
    var comps = document.querySelectorAll('cart-items-component[data-section-id]');
    console.log('[ARL-GIFT-DEBUG] patchSections() found', comps.length, 'component(s), response keys =', Object.keys(sectionsObj));
    comps.forEach(function (cur) {
      var id = cur.getAttribute('data-section-id');
      var html = sectionsObj[id];
      if (typeof html !== 'string') {
        console.warn('[ARL-GIFT-DEBUG] no section HTML returned for id', id);
        return;
      }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('cart-items-component[data-section-id="' + id + '"]') || doc.querySelector('cart-items-component');
      if (fresh) {
        console.log('[ARL-GIFT-DEBUG] patching innerHTML for', id, 'at', Date.now());
        cur.innerHTML = fresh.innerHTML;
      } else {
        console.warn('[ARL-GIFT-DEBUG] could not find fresh cart-items-component in returned HTML for', id);
      }
    });
  }

  function getSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (c) {
      var id = c.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function computeCartState(cart) {
    var realsQty = 0;
    var realsSubtotal = 0; // cents
    var giftQtyById = {};
    (cart.items || []).forEach(function (it) {
      if (isGiftLine(it)) {
        giftQtyById[it.id] = (giftQtyById[it.id] || 0) + it.quantity;
      } else {
        realsQty += it.quantity;
        realsSubtotal += it.line_price;
      }
    });
    return { realsQty: realsQty, realsSubtotal: realsSubtotal, giftQtyById: giftQtyById };
  }

  /* ---------- "$X away from a free gift" progress widget ---------- */
  function formatMoney(cents, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  var GIFT_ICON_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9M12 8v13M12 8S9.5 3 7 5s2 3 5 3zM12 8s2.5-5 5-3-2 3-5 3z"/></svg>';

  var progressStylesInjected = false;
  function ensureProgressStyles() {
    if (progressStylesInjected) return;
    progressStylesInjected = true;
    var style = document.createElement('style');
    style.textContent =
      '.arl-gift-progress{background:#fdeceb;border-radius:12px;padding:16px;margin-top:16px;margin-bottom:16px;}' +
      '.arl-gift-progress__header{display:flex;align-items:center;gap:10px;margin-bottom:12px;}' +
      '.arl-gift-progress__icon{flex:0 0 auto;display:flex;color:#c81e1e;}' +
      '.arl-gift-progress__header--done .arl-gift-progress__icon{color:#1a7a3d;}' +
      '.arl-gift-progress__text{color:#1a1a1a;font-size:14px;line-height:1.4;}' +
      '.arl-gift-progress__text strong{color:#c81e1e;}' +
      '.arl-gift-progress__header--done .arl-gift-progress__text strong{color:#1a7a3d;}' +
      '.arl-gift-progress__track{height:8px;border-radius:999px;background:#f6d5d3;overflow:hidden;}' +
      '.arl-gift-progress__fill{height:100%;background:#c81e1e;border-radius:999px;transition:width .3s ease;}' +
      '.arl-gift-progress__fill--done{background:#1a7a3d;}' +
      '.arl-gift-progress__footer{display:flex;justify-content:space-between;margin-top:8px;font-size:12px;color:#8a6a68;letter-spacing:.02em;}' +
      '.arl-gift-progress__goal{font-weight:600;}';
    document.head.appendChild(style);
  }

  // Renders into whichever cart summary container(s) are present — drawer
  // and/or full cart page. These containers live INSIDE the cart-items-
  // component's own markup, so patchSections()'s innerHTML swap wipes this
  // widget out along with everything else; it must be re-rendered after
  // every DOM patch, which enforce() already does on every pass below.
  function renderGiftProgress(cart) {
    ensureProgressStyles();
    var state = computeCartState(cart);
    var currencyCode = cart.currency && cart.currency.active;

    var thresholdGifts = GIFTS.filter(function (g) { return g.minSubtotal > 0; })
      .sort(function (a, b) { return a.minSubtotal - b.minSubtotal; });
    var pendingTier = thresholdGifts.filter(function (g) { return state.realsSubtotal < g.minSubtotal; })[0] || null;
    var achievedTier = !pendingTier
      ? (thresholdGifts.filter(function (g) { return (state.giftQtyById[g.id] || 0) > 0; })
          .sort(function (a, b) { return b.minSubtotal - a.minSubtotal; })[0] || null)
      : null;

    // Below the product list, not inside the order-summary card — simpler
    // and avoids that card's CSS Grid subgrid layout entirely.
    var containers = document.querySelectorAll('.cart-drawer__items, .cart-page__items');
    containers.forEach(function (container) {
      var widget = container.querySelector('.arl-gift-progress');
      if (!pendingTier && !achievedTier) {
        if (widget) widget.remove();
        return;
      }
      if (!widget) {
        widget = document.createElement('div');
        widget.className = 'arl-gift-progress';
        container.appendChild(widget);
      }
      if (pendingTier) {
        var remaining = pendingTier.minSubtotal - state.realsSubtotal;
        var pct = Math.max(0, Math.min(100, (state.realsSubtotal / pendingTier.minSubtotal) * 100));
        widget.innerHTML =
          '<div class="arl-gift-progress__header">' +
            '<span class="arl-gift-progress__icon">' + GIFT_ICON_SVG + '</span>' +
            '<span class="arl-gift-progress__text"><strong>' + esc(formatMoney(remaining, currencyCode)) + '</strong> away from a free <strong>' + esc(pendingTier.name) + '</strong>.</span>' +
          '</div>' +
          '<div class="arl-gift-progress__track"><div class="arl-gift-progress__fill" style="width:' + pct + '%"></div></div>' +
          '<div class="arl-gift-progress__footer"><span>' + esc(formatMoney(state.realsSubtotal, currencyCode)) + '</span><span class="arl-gift-progress__goal">GOAL ' + esc(formatMoney(pendingTier.minSubtotal, currencyCode)) + '</span></div>';
      } else {
        widget.innerHTML =
          '<div class="arl-gift-progress__header arl-gift-progress__header--done">' +
            '<span class="arl-gift-progress__icon">' + GIFT_ICON_SVG + '</span>' +
            '<span class="arl-gift-progress__text">You unlocked a free <strong>' + esc(achievedTier.name) + '</strong>!</span>' +
          '</div>' +
          '<div class="arl-gift-progress__track"><div class="arl-gift-progress__fill arl-gift-progress__fill--done" style="width:100%"></div></div>';
      }
    });
    console.log('[ARL-GIFT-DEBUG] renderGiftProgress()', pendingTier ? 'pending:' + pendingTier.name : (achievedTier ? 'achieved:' + achievedTier.name : 'none (no threshold tiers configured)'));
  }

  /* ---------- enforce the invariant, per tier ---------- */
  function enforce(options) {
    options = options || {};
    if (busy) { queued = true; return Promise.resolve(); }
    busy = true;
    console.log('[ARL-GIFT-DEBUG] enforce() start', options, 'at', Date.now());
    return jget(u('cart.js')).then(function (cart) {
      var state = computeCartState(cart);
      var realsQty = state.realsQty;
      var realsSubtotal = state.realsSubtotal;
      var giftQtyById = state.giftQtyById;

      var toAdd = [];
      var updates = {};
      var addedTiers = [];
      var removingAny = false;

      GIFTS.forEach(function (g) {
        var currentQty = giftQtyById[g.id] || 0;
        var eligible = realsQty >= 1 && realsSubtotal >= g.minSubtotal;

        if (eligible && currentQty === 0) {
          toAdd.push({ id: g.id, quantity: 1 });
          addedTiers.push(g);
        } else if (eligible && currentQty > 1) {
          updates[g.id] = 1;
        } else if (!eligible && currentQty > 0) {
          updates[g.id] = 0;
          removingAny = true;
        }
      });

      console.log('[ARL-GIFT-DEBUG] realsQty=' + realsQty, 'realsSubtotal=' + realsSubtotal, 'giftQtyById=', giftQtyById, 'toAdd=', toAdd, 'updates=', updates);

      var hasChange = toAdd.length > 0 || Object.keys(updates).length > 0;
      if (!hasChange) {
        console.log('[ARL-GIFT-DEBUG] no change needed, exiting at', Date.now());
        updateCounts(cart.item_count);
        renderGiftProgress(cart);
        return null;
      }

      // Reload only on a user-triggered pass where the cart's real items are
      // empty and we're removing gift(s) — lets the proper empty state render.
      // The silent load-time pass never sets userTriggered → no reload loop.
      var reloadOnEmpty = realsQty === 0 && removingAny && options.userTriggered;

      var sectionIds = getSectionIds();
      var sectionsParam = sectionIds.join(',');
      var lastSections = null;

      var chain = Promise.resolve();
      if (toAdd.length) {
        chain = chain.then(function () {
          return jpost(u('cart/add.js'), { items: toAdd, sections: sectionsParam, sections_url: window.location.pathname });
        }).then(function (json) {
          console.log('[ARL-GIFT-DEBUG] cart/add.js response keys =', json && Object.keys(json));
          if (json && json.sections) lastSections = json.sections;
          return json;
        });
      }
      if (Object.keys(updates).length) {
        chain = chain.then(function () {
          return jpost(u('cart/update.js'), { updates: updates, sections: sectionsParam, sections_url: window.location.pathname });
        }).then(function (json) {
          console.log('[ARL-GIFT-DEBUG] cart/update.js response keys =', json && Object.keys(json));
          if (json && json.sections) lastSections = json.sections;
          return json;
        });
      }

      return chain.then(function () {
        console.log('[ARL-GIFT-DEBUG] mutation(s) applied server-side at', Date.now());
        if (reloadOnEmpty) { window.location.reload(); return; }
        // Always re-fetch the full cart (not just item_count) so the
        // progress widget has accurate, current totals to render from.
        return jget(u('cart.js')).then(function (freshCart) {
          updateCounts(freshCart.item_count);
          patchSections(lastSections);
          renderGiftProgress(freshCart);
          console.log('[ARL-GIFT-DEBUG] DOM patch done at', Date.now());
          if (!options.silent) {
            addedTiers.forEach(function (g) {
              toast({ gift: true, title: g.title, message: g.toast });
            });
          }
        });
      });
    }).catch(function (err) { console.warn('[ARL-GIFT-DEBUG] enforce() error', err); }).then(function () {
      busy = false;
      if (queued) { queued = false; return enforce({ silent: true }); }
    });
  }

  /* ---------- intercept every cart mutation ---------- */
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var isMutation = CART_MUTATION.test(url);
    var promise = realFetch(input, init);

    if (isMutation) {
      if (/\/cart\/add(\.js)?/.test(url)) {
        promise.then(function (res) {
          res.clone().json().then(function (data) {
            var items = data && data.items ? data.items : [data];
            var real = (items || []).filter(function (it) { return it && GIFT_IDS.indexOf(it.id) === -1 && GIFT_IDS.indexOf(it.variant_id) === -1; });
            if (real.length) toast({ title: 'Added to cart', message: real[0].product_title || real[0].title || '' });
          }).catch(function () {});
        }).catch(function () {});
      }
      // Fabric's cart-items-component reacts to its OWN cart_change/update
      // requests too (same URLs we intercept), doing a fast single-request
      // DOM morph from that response's HTML. Our enforcement is slower
      // (fetch cart.js -> maybe mutate -> fetch sections -> patch DOM), so
      // without a delay the two can race on quantity +/- and Fabric's morph
      // — built from a snapshot taken BEFORE our correction — can land after
      // ours and silently revert it. That race is specific to change/update/
      // clear (the stepper's endpoint); a plain add-to-cart never goes
      // through that component, so it stays instant — no reason to make
      // "you just unlocked a gift" feel sluggish for the common case.
      var isQuantityEndpoint = /\/cart\/(change|update|clear)(\.js)?(\?|#|$)/.test(url);
      var enforceDelay = isQuantityEndpoint ? 500 : 0;
      promise.then(
        function () { setTimeout(function () { enforce({ userTriggered: true }); }, enforceDelay); },
        function () { setTimeout(function () { enforce({ silent: true }); }, enforceDelay); }
      );
    }
    return promise;
  };

  /* ---------- hide gift products from predictive search (exact handle match) ---------- */
  function isGiftLink(href) {
    if (!href || !GIFT_HANDLES.length) return false;
    try {
      var path = new URL(href, window.location.origin).pathname;
      var m = path.match(/\/products\/([^\/?#]+)/);
      return !!(m && GIFT_HANDLES.indexOf(decodeURIComponent(m[1])) !== -1);
    } catch (e) { return false; }
  }
  function scrubPredictive(root) {
    if (!GIFT_HANDLES.length) return;
    root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      if (isGiftLink(a.getAttribute('href'))) {
        var card = a.closest('li, .predictive-search-results__card');
        if (card) card.remove();
      }
    });
  }
  function initSearchScrub() {
    if (!GIFT_HANDLES.length) return;
    document.querySelectorAll('predictive-search-component').forEach(function (c) {
      scrubPredictive(c);
      new MutationObserver(function () { scrubPredictive(c); }).observe(c, { childList: true, subtree: true });
    });
  }

  /* ---------- load-time repair (silent, no toast, no reload) ---------- */
  function boot() {
    enforce({ silent: true });
    initSearchScrub();
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();